import React, { useState } from 'react';
import { Download, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { generatePDFReport, downloadBlob } from '../services/api';
import CompanyReport from './report/CompanyReport';
import PortfolioPanel from './report/PortfolioPanel';
import { RiskDot, conditionFromZone } from './report/primitives';
import { useCaseLabel } from './report/decisionMapping';
import '../styles/report.css';

/**
 * Results shell: sticky command bar (lens badge, new analysis, PDF),
 * portfolio for multi-company batches, then one briefing per company.
 * The analysis lens is fixed at request time — no post-hoc Corporate/Investor
 * toggle that would show mismatched narrative.
 */
const AnalysisResults = ({ results, onNewAnalysis }) => {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);

  const companies = results.results;
  const showRail = companies.length > 3;
  const useCase = results.useCase || 'vendor';

  const handleDownloadPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      toast.loading('Generating Counterparty Opinion PDF…', { id: 'pdf-generation' });

      const pdfBlob = await generatePDFReport(results);
      const first = companies[0];
      const opinionNumber =
        first?.opinionNumber ||
        first?.analysis?.aiAnalysis?.opinionNumber ||
        first?.analysis?.dataLineage?.opinionNumber;
      const opinionDate =
        first?.opinionDate ||
        first?.analysis?.aiAnalysis?.opinionDate ||
        first?.analysis?.dataLineage?.opinionDate ||
        new Date().toISOString().split('T')[0];
      const companyName = first?.company?.name || 'Counterparty';
      const filename =
        companies.length === 1 && opinionNumber != null
          ? `Novrick Opinion ${opinionNumber} — ${companyName} — ${opinionDate}.pdf`
          : `Novrick Opinions — ${opinionDate}.pdf`;

      downloadBlob(pdfBlob, filename);

      toast.success('Counterparty Opinion downloaded', { id: 'pdf-generation' });
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error(
        error?.message || 'The Opinion PDF could not be prepared. Please try again.',
        { id: 'pdf-generation' }
      );
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const anchorFor = (company, index) =>
    `report-${(company.company.ticker || company.company.name || index)
      .toString()
      .replace(/[^a-zA-Z0-9]/g, '-')}-${index}`;

  const scrollToReport = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="fr-results">
      <div className="fr-shellbar">
        <div className="container">
          <div className="fr-shellbar-inner">
            <div className="fr-shellbar-title">
              <h2>Counterparty Opinion</h2>
              <span className="fr-shellbar-sub fr-num">
                {results.successfulAnalyses}{' '}
                {results.successfulAnalyses === 1 ? 'counterparty' : 'counterparties'}
                {' \u00b7 '}completed in {(results.processingTime / 1000).toFixed(1)}s
              </span>
              <span className="fr-lens-badge">{useCaseLabel(useCase)}</span>
            </div>

            <div className="fr-shellbar-actions">
              <button type="button" className="fr-btn" onClick={onNewAnalysis}>
                <RefreshCw size={14} />
                New Analysis
              </button>
              <button
                type="button"
                className="fr-btn fr-btn-primary"
                onClick={handleDownloadPDF}
                disabled={isGeneratingPDF}
              >
                <Download size={14} />
                {isGeneratingPDF ? 'Preparing\u2026' : 'Download PDF'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        {results.successfulAnalyses > 1 && (
          <PortfolioPanel
            summary={results.portfolioSummary}
            totalCompanies={results.successfulAnalyses}
            companies={companies}
          />
        )}

        <div className={`fr-layout ${showRail ? '' : 'fr-layout-single'}`}>
          {showRail && (
            <nav className="fr-rail" aria-label="Company index">
              {companies.map((company, index) => {
                const condition = conditionFromZone(
                  company.analysis.distressScore.riskZone,
                  company.analysis.aiAnalysis?.committeeDecision?.overallFinancialHealth ||
                    company.analysis.aiAnalysis?.condition
                );
                const id = anchorFor(company, index);
                return (
                  <button
                    key={id}
                    type="button"
                    className="fr-rail-item"
                    onClick={() => scrollToReport(id)}
                  >
                    <RiskDot tone={condition.tone} />
                    <span>{company.company.name}</span>
                  </button>
                );
              })}
            </nav>
          )}

          <div>
            {companies.map((company, index) => (
              <CompanyReport
                key={anchorFor(company, index)}
                anchorId={anchorFor(company, index)}
                company={{ ...company, billing: results.billing }}
                useCase={useCase}
                defaultEvidenceOpen={companies.length <= 3}
              />
            ))}

            {results.warnings && results.warnings.length > 0 && (
              <div className="fr-warnings">
                {results.warnings.map((warning, index) => (
                  <div key={index} className="fr-caveat-strip">
                    <span>
                      <strong>{warning.company}:</strong> {warning.error}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="fr-disclaimer" id="disclaimer">
              <p>
                This analysis is decision-support only and is not legal, tax, accounting, or
                investment advice.{' '}
                {!disclaimerOpen && (
                  <button type="button" onClick={() => setDisclaimerOpen(true)}>
                    Full disclaimer
                  </button>
                )}
              </p>
              {disclaimerOpen && (
                <p style={{ marginTop: '0.4rem' }}>
                  The analysis is based on third-party public data and stated assumptions, and does
                  not guarantee future performance. You are responsible for independently verifying
                  figures and applying this output to your specific situation. Data sources include
                  public financial statements and market data; completeness and timeliness of all
                  information presented cannot be guaranteed. Internal trend reflects change versus
                  the company&apos;s own reported history.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalysisResults;
